import { isEmpty } from "./BasicUtilities";
import { zonedInputToIso } from "./DateUtilities";
import { TIMEZONES } from "./Constants";

export function constructSignupPayload(name, email, password, phone, referralToken) {
    let payload = {
        name,
        email,
        password,
    };
    if (phone && phone.length > 3) {
        payload["phone"] = phone;
    }
    if (referralToken && referralToken.length > 0) {
        payload["referral_token"] = referralToken;
    }
    return payload;
}


export function constructLoginPayload(email, password) {
    return { email, password };
}

export function constructSelectPlanPayload(planCode, promoCode = null) {
    const payload = { plan_code: planCode };
    if (!isEmpty(promoCode)) payload.promo_code = promoCode;
    return payload;
}

// M2 purchase: plan + optional promo + optional saved bKash account. `provider` selects the
// payment gateway (Stripe card checkout for now; bKash/PayPal later). `savedCardId` (Stripe)
// charges an existing saved card instead of opening hosted checkout. `upgradeTarget`
// ("current" | "scheduled") chooses which plan to upgrade when both exist.
export function constructPurchasePayload({ planCode, promoCode, bkashAccountId, provider = 'stripe', savedCardId, upgradeTarget }) {
    const payload = { plan_code: planCode, provider };
    if (!isEmpty(promoCode)) payload.promo_code = promoCode;
    if (bkashAccountId) payload.bkash_account_id = Number(bkashAccountId);
    if (savedCardId) payload.saved_card_id = Number(savedCardId);
    if (upgradeTarget) payload.upgrade_target = upgradeTarget;
    return payload;
}

// ---------- Promo / coupon codes ----------
export function constructPromoCodePayload({
    code, description, partnerLabel, discountType, discountValue, appliesTo,
    maxRedemptions, perUserLimit, validFrom, expiresAt, isActive,
}) {
    return {
        code: (code || "").trim().toUpperCase(),
        description: description || "",
        partner_label: partnerLabel || "",
        discount_type: discountType || "PERCENT",
        discount_value: Number(discountValue) || 0,
        applies_to: Array.isArray(appliesTo) ? appliesTo : [],
        max_redemptions: isEmpty(maxRedemptions) ? null : Number(maxRedemptions),
        per_user_limit: isEmpty(perUserLimit) ? 1 : Number(perUserLimit),
        valid_from: validFrom ? new Date(validFrom).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        is_active: !!isActive,
    };
}

export function constructPromoValidatePayload(code, planCode) {
    return { code: (code || "").trim().toUpperCase(), plan_code: planCode };
}

// ---------- Super-admin: awards ----------
export function constructAwardPayload({ userId, recipientPhone, amountBdt, category, level, reason, sendEmail, emailSubject, emailBody }) {
    const payload = { user_id: Number(userId), amount_bdt: Number(amountBdt) || 0 };
    if (!isEmpty(recipientPhone)) payload.recipient_phone = recipientPhone;
    if (!isEmpty(category)) payload.category = category;
    if (!isEmpty(level)) payload.level = level;
    if (!isEmpty(reason)) payload.reason = reason;
    payload.send_email = sendEmail !== false;
    if (!isEmpty(emailSubject)) payload.email_subject = emailSubject;
    if (!isEmpty(emailBody)) payload.email_body = emailBody;
    return payload;
}

export function constructAwardEmailPayload(subject, body) {
    return { subject: subject || "", body: body || "" };
}

export function constructAwardCommentPayload(comment) {
    return { admin_comment: comment || "" };
}

// Finalize (approve + pay) a referral reward — amount + bKash number editable at payout.
export function constructAwardFinalizePayload({ amountBdt, recipientPhone }) {
    const payload = {};
    if (!isEmpty(amountBdt)) payload.amount_bdt = Number(amountBdt);
    if (!isEmpty(recipientPhone)) payload.recipient_phone = recipientPhone;
    return payload;
}

// ---------- Referrals (G1) ----------
export function constructReferralInvitePayload(invitedEmail) {
    return { invited_email: (invitedEmail || "").trim() };
}

export function constructReferralGrantPayload({ planCode, days, reason }) {
    return { plan_code: planCode, days: Number(days) || 0, reason: reason || "" };
}

// ---------- Super-admin: payments dashboard refund (G3) ----------
export function constructPaymentRefundPayload({ amountBdt, recipientPhone, reason, cancelSubscription }) {
    const payload = { cancel_subscription: !!cancelSubscription };
    if (!isEmpty(amountBdt)) payload.amount_bdt = Number(amountBdt);
    if (!isEmpty(recipientPhone)) payload.recipient_phone = recipientPhone;
    if (!isEmpty(reason)) payload.reason = reason;
    return payload;
}

// ---------- Rewards / stars (G2) ----------
export function constructRewardGrantPayload({ userId, stars, reason }) {
    return { user_id: Number(userId), stars: Number(stars) || 0, reason: reason || "" };
}

export function constructRewardGrantBulkPayload({ q, period, stars, reason }) {
    const payload = { stars: Number(stars) || 0, reason: reason || "" };
    if (!isEmpty(q)) payload.q = q;
    if (!isEmpty(period)) payload.period = period;
    return payload;
}

// ---------- Super-admin: user management ----------
export function constructUserUpdatePayload({ name, phone, institute, isActive }) {
    const payload = {};
    if (name !== undefined && name !== null) payload.name = name;
    if (phone !== undefined && phone !== null) payload.phone = phone;
    if (institute !== undefined && institute !== null) payload.institute = institute;
    if (isActive !== undefined && isActive !== null) payload.is_active = isActive;
    return payload;
}

export function constructAdminSubscriptionPayload({ action, planCode, expiresAt, subscriptionId }) {
    const payload = { action };
    if (!isEmpty(planCode)) payload.plan_code = planCode;
    if (!isEmpty(expiresAt)) payload.expires_at = new Date(expiresAt).toISOString();
    if (subscriptionId) payload.subscription_id = Number(subscriptionId);
    return payload;
}

export function constructRefundPayload({ amountBdt, recipientPhone, reason, cancelSubscription, sendEmail, emailSubject, emailBody }) {
    const payload = { amount_bdt: Number(amountBdt) || 0, cancel_subscription: !!cancelSubscription, send_email: !!sendEmail };
    if (!isEmpty(recipientPhone)) payload.recipient_phone = recipientPhone;
    if (!isEmpty(reason)) payload.reason = reason;
    if (!isEmpty(emailSubject)) payload.email_subject = emailSubject;
    if (!isEmpty(emailBody)) payload.email_body = emailBody;
    return payload;
}

export function constructUserMessagePayload(subject, body) {
    return { subject: subject || "", body: body || "" };
}

export function constructUserDeletePayload(scope, confirmEmail) {
    const payload = { scope };
    if (!isEmpty(confirmEmail)) payload.confirm_email = confirmEmail;
    return payload;
}

// ---------- Payments ----------
export function constructBkashAccountPayload({ accountNumber, label }) {
    const payload = { account_number: accountNumber };
    if (label !== undefined && label !== null) payload.label = label;
    return payload;
}

export function constructBkashAccountUpdatePayload({ label, isDefault }) {
    const payload = {};
    if (label !== undefined && label !== null) payload.label = label;
    if (isDefault !== undefined && isDefault !== null) payload.is_default = isDefault;
    return payload;
}

export function constructGoogleAuthPayload(credential, referralToken) {
    const payload = { credential };
    if (referralToken && referralToken.length > 0) {
        payload["referral_token"] = referralToken;
    }
    return payload;
}


export function constructRefreshTokenRenewPayload(oldRefreshToken) {
    let payload = {
        old_refresh_token: oldRefreshToken
    };
    return payload;
}

// ---------- Profile / password ----------
export function constructProfileUpdatePayload({ name, phone, institute, profilePicture, hideInstituteOnLeaderboard }) {
    const payload = {};
    if (name !== undefined && name !== null) payload.name = name;
    if (phone !== undefined && phone !== null) payload.phone = phone;
    if (institute !== undefined && institute !== null) payload.institute = institute;
    if (profilePicture !== undefined && profilePicture !== null) payload.profile_picture = profilePicture;
    if (hideInstituteOnLeaderboard !== undefined && hideInstituteOnLeaderboard !== null) {
        payload.hide_institute_on_leaderboard = hideInstituteOnLeaderboard;
    }
    return payload;
}

export function constructChangePasswordPayload(oldPassword, newPassword) {
    return { old_password: oldPassword, new_password: newPassword };
}

export function constructForgotPasswordPayload(email) {
    return { email };
}

export function constructResetPasswordPayload(token, newPassword) {
    return { token, new_password: newPassword };
}

export function constructVerifyEmailPayload(token) {
    return { token };
}

// ---------- Notices ----------
// "Show from" / "Hide after" are entered as PST wall-clock times (see NoticeManagement) and
// stored as UTC instants, so the visibility window is judged in PST regardless of who set it.
export function constructNoticePayload({ title, body, noticeType, level, isPublished, isPinned, startsAt, endsAt }) {
    const payload = {
        title,
        body: body || "",
        notice_type: noticeType || "generic",
        level: level || "",
        is_published: !!isPublished,
        is_pinned: !!isPinned,
        starts_at: zonedInputToIso(startsAt, TIMEZONES.PST),
        ends_at: zonedInputToIso(endsAt, TIMEZONES.PST),
    };
    return payload;
}


export function constructJwtTokenRenewPayload(refreshToken) {
    let payload = {
        refresh_token: refreshToken
    };
    return payload;
}

export function constructTagPayload(name, level, type, metadata, isCurrent, subject) {
    let payload = {
        name: name,
        tag_type: type
    };
    if (level && level.length > 0) {
        payload["level"] = level;
    }
    // First-class subject for TOPIC tags (authoritative topic→subject link).
    if (subject && subject.length > 0) {
        payload["subject"] = subject;
    }
    if (metadata && Object.keys(metadata).length > 0) {
        payload["metadata"] = metadata;
    }
    if (isCurrent !== undefined && isCurrent !== null) {
        payload["is_current"] = isCurrent;
    }
    return payload
}

export function constructTagDeletePayload(tagIdarray) {
    let payload = {
        items: tagIdarray.map(id => ({ tag_id: id }))
    };
    return payload
}

export function constructTagReferencesPayload(tagIdArray) {
    return { tag_ids: tagIdArray };
}

export function constructQuestionPayload() {
    let payload = {
        // add the payload field
    };
    return payload
}

// Object-based builder for the admin question create/update payload. Callers pass a
// fields object; only meaningful values are sent (empty optionals become null/omitted).
// `correctOption` is a comma-joined set (e.g. "A" or "A,C") to support multiple answers.
export function constructQuestionPostPayload(fields) {
    const {
        text, options = {}, optionImages = {}, correctOption, answerExplanation,
        level, levels, topic, subject, exam, difficulty, year, bcsTerm, syllabusStatus,
        questionType, textAnswer, board, division, unit, sideText, audioUrl,
        solvingTimeSeconds, questionImageUrl, explanationImageUrl, metadata, groupId, groupOrder,
    } = fields;

    // Option text (a–h) is always sent so clearing an option persists; blank = absent.
    const OPTION_FIELD_KEYS = ["option_a", "option_b", "option_c", "option_d", "option_e", "option_f", "option_g", "option_h"];
    // Multi-level: send the full `levels` list; `level` stays the primary (levels[0]) for
    // backward compatibility with any single-level consumer.
    const levelList = Array.isArray(levels) && levels.length ? levels : (level ? [level] : []);
    const payload = {
        question_text: text,
        correct_option: correctOption || "",
        question_type: questionType || "MCQ",
        level: levelList[0] || level || "",
        levels: levelList,
        subject: subject || "",
        difficulty: difficulty,
    };
    OPTION_FIELD_KEYS.forEach(k => { payload[k] = options[k] || ""; });
    if (answerExplanation && answerExplanation.length > 0) payload["answer_explanation"] = answerExplanation;
    if (textAnswer && textAnswer.length > 0) payload["text_answer"] = textAnswer;
    payload["topic"] = topic && topic.length > 0 ? topic : null;
    payload["exam"] = exam && exam.length > 0 ? exam : null;
    payload["year"] = year && year.length > 0 ? year : null;
    payload["bcs_term"] = bcsTerm && bcsTerm.length > 0 ? bcsTerm : null;
    if (board && board.length > 0) payload["board"] = board;
    if (division && division.length > 0) payload["division"] = division;
    if (unit && unit.length > 0) payload["unit"] = unit;
    if (sideText && sideText.length > 0) payload["side_text"] = sideText;
    if (audioUrl && audioUrl.length > 0) payload["audio_url"] = audioUrl;
    if (solvingTimeSeconds !== undefined && solvingTimeSeconds !== null && solvingTimeSeconds !== "") {
        payload["solving_time_seconds"] = Number(solvingTimeSeconds);
    }
    if (questionImageUrl) payload["question_image_url"] = questionImageUrl;
    OPTION_FIELD_KEYS.forEach(k => { if (optionImages[k]) payload[`${k}_image_url`] = optionImages[k]; });
    if (explanationImageUrl) payload["explanation_image_url"] = explanationImageUrl;
    if (syllabusStatus && syllabusStatus.length > 0) payload["syllabus_status"] = syllabusStatus;
    // Text Completion blanks (and any other structured extras) travel in metadata.
    if (metadata !== undefined && metadata !== null) payload["metadata"] = metadata;
    // Passage-group binding (passage-authoring screen creates child questions bound to a group).
    if (groupId !== undefined && groupId !== null) {
        payload["group_id"] = groupId;
        payload["group_order"] = groupOrder !== undefined && groupOrder !== null && groupOrder !== "" ? Number(groupOrder) : null;
    }
    return payload;
}

// --- Passage question groups ---
export function constructQuestionGroupPayload({ title, passageText, audioUrl, level, subject, topic, exam, division, unit, year, bcsTerm, metadata }) {
    const payload = { level };
    if (title && title.length > 0) payload.title = title;
    if (passageText && passageText.length > 0) payload.passage_text = passageText;
    if (audioUrl && audioUrl.length > 0) payload.audio_url = audioUrl;
    if (subject && subject.length > 0) payload.subject = subject;
    if (topic && topic.length > 0) payload.topic = topic;
    if (exam && exam.length > 0) payload.exam = exam;
    if (division && division.length > 0) payload.division = division;
    if (unit && unit.length > 0) payload.unit = unit;
    if (year && year.length > 0) payload.year = year;
    if (bcsTerm && bcsTerm.length > 0) payload.bcs_term = bcsTerm;
    if (metadata) payload.metadata = metadata;
    return payload;
}

export function constructGroupAttachPayload(questionIds) {
    return { question_ids: questionIds };
}

export function constructGroupDetachPayload(questionIds) {
    return { question_ids: questionIds };
}

export function constructGroupReorderPayload(orderedQuestionIds) {
    return { ordered_question_ids: orderedQuestionIds };
}

// Bulk-set syllabus status: by explicit ids, or a level[+subject/topic/year] sweep.
export function constructBulkSyllabusStatusPayload({ status, ids, level, subject, topic, year }) {
    const payload = { status };
    if (Array.isArray(ids) && ids.length > 0) payload.ids = ids;
    if (!isEmpty(level)) payload.level = level;
    if (!isEmpty(subject)) payload.subject = subject;
    if (!isEmpty(topic)) payload.topic = topic;
    if (!isEmpty(year)) payload.year = String(year);
    return payload;
}

export function constructQuizPostPayload(title, description, startTime, durationMinutes, level, createdBy, type, questionIds, setId, year, opts = {}) {
    let payload = {
        title: title,
        created_by: createdBy,
        quiz_type: type,
        question_ids: questionIds,
        set_id: setId,
        level: level
    }
    // Duration is optional: when blank the backend auto-allocates for eligible BCS types.
    if (!isEmpty(durationMinutes)) payload["duration_minutes"] = Number(durationMinutes);
    if (!isEmpty(opts.division)) payload["division"] = opts.division;
    if (!isEmpty(opts.unit)) payload["unit"] = opts.unit;
    // Free-form structure blob — TOEFL model tests pass their section/module/task blueprint here.
    if (opts.metadata) payload["metadata"] = opts.metadata;
    if (!isEmpty(description)) {
        payload["description"] = description;
    }
    if (type === 'previous_year' && !isEmpty(year)) {
        payload["year"] = Number(year);
    }
    if (!isEmpty(startTime)) {
        startTime = new Date(startTime);
        payload["start_time"] = startTime;
        if (!isEmpty(durationMinutes)) {
            payload["end_time"] = new Date(startTime.getTime() + Number(durationMinutes) * 60000); // 60000 ms = 1 min
        }
    }

    return payload;
}

export function constructQuizDetailsPayload(title, description) {
    // Inline Manage Tests grid edit — title/description only.
    return {
        title: title,
        description: isEmpty(description) ? null : description,
    };
}

export function getAdminQuizDeletePayload(quizIdArray) {
    let payload = {
        items: quizIdArray.map(id => ({ quiz_id: id, set_id: 1 }))
    };
    return payload
}

// Auto Quiz Build: split selected model tests into per-section micro quizzes.
export function constructSplitModelTestPayload(sourceQuizIds, sections) {
    return { source_quiz_ids: sourceQuizIds, sections: sections };
}

// Auto Quiz Build: generate a competition quiz (random Reading + Listening micro).
export function constructGenerateCompetitionPayload(level, openHours, readingMinutes, listeningMinutes) {
    const payload = { level: level, open_hours: Number(openHours) };
    if (readingMinutes) payload.reading_minutes = Number(readingMinutes);
    if (listeningMinutes) payload.listening_minutes = Number(listeningMinutes);
    return payload;
}

// Auto Quiz Build (GRE): assemble a competition (Quant + Verbal + Quant) from published tests.
export function constructGreCompetitionPayload(openHours) {
    return { open_hours: Number(openHours) };
}

// Auto Quiz Build (BCS): full model test from a per-subject distribution {subject: count}.
// `durationMinutes` is omitted when blank so the backend auto-allocates (ceil(0.6 × total)).
export function constructBcsPublishedPayload(distribution, durationMinutes) {
    const clean = {};
    Object.entries(distribution || {}).forEach(([subject, count]) => {
        const n = Number(count);
        if (Number.isFinite(n) && n > 0) clean[subject] = Math.round(n);
    });
    const payload = { distribution: clean };
    const d = Number(durationMinutes);
    if (Number.isFinite(d) && d >= 1) payload.duration_minutes = Math.round(d);
    return payload;
}

// Competition: incrementally save in-progress answers (server backstop for 2h auto-submit).
export function constructSaveAnswersPayload(userAnswers) {
    const answers = Object.entries(userAnswers || {}).map(([question_id, val]) => {
        if (Array.isArray(val)) return { question_id: Number(question_id), selected_option: val.slice().sort().join(',') };
        if (val && typeof val === 'object') return { question_id: Number(question_id), text_answer: val.text || '', selected_option: Array.isArray(val.order) ? val.order.join(',') : '' };
        if (typeof val === 'string' && /^[A-H](,[A-H])*$/i.test(val)) return { question_id: Number(question_id), selected_option: val.toUpperCase() };
        return { question_id: Number(question_id), text_answer: val == null ? '' : String(val) };
    });
    return { answers };
}

// ---------- Quiz publish / archive (admin) ----------
export function constructQuizPublishPayload(isPublished) {
    return { is_published: !!isPublished };
}

export function constructQuizPublishBulkPayload(quizIds, isPublished) {
    return { quiz_ids: quizIds, is_published: !!isPublished };
}

export function constructQuizArchivePayload(isArchived) {
    return { is_archived: !!isArchived };
}

export function constructQuizArchiveBulkPayload(quizIds, isArchived) {
    return { quiz_ids: quizIds, is_archived: !!isArchived };
}

// Map the anonymous homepage quiz history (kobra_anon_quiz_history) into the
// shape POST /submission/import-anonymous expects. Includes per-question answers
// when present so the imported attempt supports full review.
export function constructAnonymousImportPayload(history) {
    return {
        items: (history || [])
            .filter(h => h && h.date && h.level)
            .map(h => ({
                date: h.date,
                level: h.level,
                total: Number(h.total) || 0,
                correct: Number(h.correct) || 0,
                answers: Array.isArray(h.answers)
                    ? h.answers.map(a => ({
                        question_id: a.question_id,
                        selected_option: a.selected_option || '',
                    }))
                    : [],
            })),
    };
}

// Record a logged-in user's homepage quick quiz (POST /submission/quick), with
// per-question answers so it supports full review on the Daily Challenge page.
export function constructQuickSubmissionPayload(level, correct, total, answers) {
    return {
        level,
        correct: Number(correct) || 0,
        total: Number(total) || 0,
        answers: Array.isArray(answers)
            ? answers.map(a => ({ question_id: a.question_id, selected_option: a.selected_option || '' }))
            : [],
    };
}

// Record a logged-in user's homepage Practice attempt (POST /submission/practice)
// so the per-plan daily practice cap can be enforced/displayed.
export function constructPracticeSubmissionPayload(level, correct, total) {
    return {
        level: level || '',
        correct: Number(correct) || 0,
        total: Number(total) || 0,
    };
}

export function getCustomQuizPostPayload(title, description, startTime, endTime, durationMinutes, createdBy, level, type, quizSubjects, maxQuestionPerSubject, quizExams, maxQuestionFromFavorite, maxQuestionFromWrongAnswer, setId, selectedQuestionSource, quizYears, quizBcsTerms, maxQuestionPerTerm, opts = {}) {
    const quizPayload = {
        title,
        duration_minutes: Number(durationMinutes),
        created_by: createdBy,
        level,
        quiz_type: type,
        set_id: setId
    };
    // Secondary dimension narrows the question pool (SSC/HSC division or VARSITY-* unit).
    if (!isEmpty(opts.division)) quizPayload["division"] = opts.division;
    if (!isEmpty(opts.unit)) quizPayload["unit"] = opts.unit;

    // Conditionally add fields based on selectedQuestionSource
    if (!isEmpty(description)) {
        quizPayload["description"] = description;
    }

    if (!isEmpty(endTime)) {
        quizPayload["end_time"] = endTime;
    }
    if (!isEmpty(startTime)) {
        quizPayload["start_time"] = startTime;
    }
    if (selectedQuestionSource === 'customSubjects') {
        quizPayload["subjects"] = quizSubjects.length > 0 ? quizSubjects : null;
        quizPayload["max_question_per_subject"] = maxQuestionPerSubject !== null ? Number(maxQuestionPerSubject) : null;
    }
    if (selectedQuestionSource === 'favoriteQuestions') {
        quizPayload["include_favorite"] = true;
        quizPayload["max_question_from_favorite"] = maxQuestionFromFavorite !== null ? Number(maxQuestionFromFavorite) : null;
    }
    if (selectedQuestionSource === 'wrongAnswerQuestions') {
        quizPayload["include_wrong_answer"] = true;
        quizPayload["max_question_from_wrong_answer"] = maxQuestionFromWrongAnswer !== null ? Number(maxQuestionFromWrongAnswer) : null;
    }
    if (selectedQuestionSource === 'bcsTerms') {
        // BCS mode can mix subject-based and term-based questions; include each
        // group only when it has been populated.
        if (quizSubjects && quizSubjects.length > 0 && maxQuestionPerSubject) {
            quizPayload["subjects"] = quizSubjects;
            quizPayload["max_question_per_subject"] = Number(maxQuestionPerSubject);
        }
        if (quizBcsTerms && quizBcsTerms.length > 0 && maxQuestionPerTerm) {
            quizPayload["bcs_terms"] = quizBcsTerms;
            quizPayload["max_question_per_term"] = Number(maxQuestionPerTerm);
        }
    }
    return quizPayload;
}

// Build the QuizPreviewIn body for POST /user-quizes/preview-counts — mirrors
// the subject/term selection so the server can return available counts per
// bucket before the quiz is created.
export function constructQuizPreviewPayload(level, subjects, maxPerSubject, bcsTerms, maxPerTerm, opts = {}) {
    const payload = { level };
    if (!isEmpty(opts.division)) payload["division"] = opts.division;
    if (!isEmpty(opts.unit)) payload["unit"] = opts.unit;
    if (subjects && subjects.length > 0 && maxPerSubject) {
        payload["subjects"] = subjects;
        payload["max_question_per_subject"] = Number(maxPerSubject);
    }
    if (bcsTerms && bcsTerms.length > 0 && maxPerTerm) {
        payload["bcs_terms"] = bcsTerms;
        payload["max_question_per_term"] = Number(maxPerTerm);
    }
    return payload;
}

export function getCustomQuizDeletePayload(quizIdArray) {
    let payload = {
        items: quizIdArray.map(id => ({ quiz_id: id, set_id: 1 }))
    };
    return payload;
}

export function getFavoriteQuestionPostPayload(questionId) {
    let payload = {
        question_id: questionId
    };
    return payload;
}

export function getQuizSubmissionDetailsPayload(quizzes) {
    const payload = [];

    for (let i = 0; i < quizzes.length; i++) {
        const quiz = quizzes[i];
        payload.push({
        quiz_id: quiz.quiz_id,
        set_id: quiz.set_id ?? 1,
        });
    }

    return payload;
}


export function constructQuizSubmissionStartPayload(quizId, setId, quizType, DurationMinutes, startTime, endTime) {
    let payload = {
        quiz_id: quizId,
        set_id: setId,
        quiz_type: quizType,
        duration_minutes: DurationMinutes
    };
    if (!isEmpty(startTime)) {
        payload["start_time"] = startTime;
    }
    if (!isEmpty(endTime)) {
        payload["end_time"] = endTime;
    }
    return payload;
}

export function constructQuizSubmissionEndPayload(submissionId, answers) {
    return {
        submission_id: submissionId,
        answers: Array.isArray(answers) ? answers : [],
    };
}

export function constructReportedQuestionPayload(questionId, reportReasons, commentForPayload) {
    const payload = {
        question_id: questionId,
        ...reportReasons
    };
    if (!isEmpty(commentForPayload)) {
        payload["comment"] = commentForPayload;
    }
    return payload;
}

export function constructReportedQuestionPutPayload(newResolutionStatus, resolutionComment) {
    let comment = resolutionComment.trim() === '' ? undefined : resolutionComment;
    let payload = {
        resolution_status: newResolutionStatus
    };
    if (!isEmpty(comment)) {
        payload["resolution_comment"] = comment;
    }

    return payload;
}

export function constructReportedQuestionStatusPayload(question_ids) {
    let payload = {
        question_ids
    };
    return payload;
}

export function constructFavoriteQuestionStatusPayload(question_ids) {
    let payload = {
        question_ids
    };
    return payload;
}

export function constructFavoriteWordStatusPayload(word_ids) {
    return { word_ids };
}

export function constructCommentPayload(comment, resolution_status) {
    let payload = {
        comment,
        resolution_status
    };
    return payload;
}

// ---------- Ingestion (CSV question-bank builder) ----------

// Payload for POST /api/ingestion/upload. `files` is an array of
// { source_file, questions: [ {question fields...} ] } parsed client-side from CSV(s).
export function constructCsvUploadPayload(level, files) {
    return { level, files };
}

// Build the payload accepted by PUT /api/ingestion/drafts/{id}. Mirrors all
// editable fields on the draft (matches apps/ingestion/schemas.py ParsedDraftUpdateIn).
// Every field must be sent: the backend handler assigns each field from the
// payload, so any omitted field falls back to its schema default and would WIPE
// the stored value (unit, passage grouping, images, question_type, …).
export function constructIngestionDraftPutPayload(draft) {
    const toIntOrNull = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    return {
        question_text: draft.question_text || '',
        option_a: draft.option_a || '',
        option_b: draft.option_b || '',
        option_c: draft.option_c || '',
        option_d: draft.option_d || '',
        option_e: draft.option_e || '',
        option_f: draft.option_f || '',
        option_g: draft.option_g || '',
        option_h: draft.option_h || '',
        correct_option: draft.correct_option || '',
        answer_explanation: draft.answer_explanation || '',
        question_type: draft.question_type || 'MCQ',
        text_answer: draft.text_answer || '',
        level: draft.level || '',
        topic: draft.topic || '',
        subject: draft.subject || '',
        exam: draft.exam || '',
        board: draft.board || '',
        division: draft.division || '',
        unit: draft.unit || '',
        bcs_term: draft.bcs_term || '',
        difficulty: draft.difficulty || 'MEDIUM',
        year: draft.year || '',
        side_text: draft.side_text || '',
        audio_url: draft.audio_url || '',
        group_key: draft.group_key || '',
        group_order: toIntOrNull(draft.group_order),
        passage_text: draft.passage_text || '',
        solving_time_seconds: toIntOrNull(draft.solving_time_seconds),
        question_image_url: draft.question_image_url || '',
        option_a_image_url: draft.option_a_image_url || '',
        option_b_image_url: draft.option_b_image_url || '',
        option_c_image_url: draft.option_c_image_url || '',
        option_d_image_url: draft.option_d_image_url || '',
        option_e_image_url: draft.option_e_image_url || '',
        option_f_image_url: draft.option_f_image_url || '',
        option_g_image_url: draft.option_g_image_url || '',
        option_h_image_url: draft.option_h_image_url || '',
        explanation_image_url: draft.explanation_image_url || '',
        // Preserve Text-Completion blanks / missing_figure flag across edits.
        metadata: draft.metadata || null,
    };
}

export function constructIngestionBulkApprovePayload(draftIds) {
    return { draft_ids: draftIds };
}

// Bulk-edit: keep only the taxonomy fields the admin actually set (non-empty).
// Untouched fields are omitted so the server leaves each row's value alone.
const BULK_EDIT_FIELDS = ['level', 'subject', 'topic', 'exam', 'board', 'division', 'unit', 'bcs_term', 'year'];
function pickBulkEditFields(fields = {}) {
    const out = {};
    BULK_EDIT_FIELDS.forEach((k) => {
        const v = fields[k];
        if (!isEmpty(v)) out[k] = k === 'year' ? String(v) : v;
    });
    return out;
}

// Bulk-edit selected parsed drafts: { draft_ids, fields }.
export function constructIngestionDraftBulkUpdatePayload(draftIds, fields) {
    return { draft_ids: draftIds, fields: pickBulkEditFields(fields) };
}

// Bulk-edit selected questions: { ids, fields }.
export function constructQuestionBulkUpdatePayload(ids, fields) {
    return { ids, fields: pickBulkEditFields(fields) };
}

// Payload for POST /api/ingestion/similar-questions. Mirrors
// SimilarQuestionsByTextIn — empty level is allowed (server returns []).
// `excludeQuestionIds` lets the caller hide the draft's own twin from the
// results when the draft was already approved.
export function constructIngestionSimilarByTextPayload(draft, topK, excludeQuestionIds = null) {
    return {
        level: draft.level || '',
        question_text: draft.question_text || '',
        option_a: draft.option_a || '',
        option_b: draft.option_b || '',
        option_c: draft.option_c || '',
        option_d: draft.option_d || '',
        top_k: topK,
        ...(excludeQuestionIds && excludeQuestionIds.length > 0
            ? { exclude_question_ids: excludeQuestionIds }
            : {}),
    };
}

// ---------- Super admin ----------

export function buildCreateAdminPayload(name, email, password) {
    return { name, email, password };
}

export function buildUpdateAdminPayload({ name, isActive, password }) {
    const payload = {};
    if (name !== undefined && name !== null) payload.name = name;
    if (isActive !== undefined && isActive !== null) payload.is_active = isActive;
    if (password !== undefined && password !== null && password.length > 0) {
        payload.password = password;
    }
    return payload;
}

export function buildMarkRevisionPayload(note) {
    return { note: note || "" };
}

export function constructPublicGradePayload(answers) {
    return {
        answers: answers.map(a => ({
            question_id: a.question_id,
            selected_option: a.selected_option || '',
            // TEXT questions carry a free-text answer (dropped previously → graded as skipped).
            text_answer: a.text_answer || '',
        })),
    };
}
